import { PreScreening } from '../pre-screening/pre-screening.model';
import { IProExam } from '../pro-exam/pro-exam.models';
import { KyeAiNote, KyeAiNoteAuditEntry, KyeFieldFinding, KyeIdentitySnapshot, KyeRotationSnapshot } from './kye.model';

export interface GetApplicationsReq {
  examId: string;
  status: 'Completed' | 'In Progress' | 'Not Started' | 'Pending Review' | 'Accepted' | 'All';
  decisionMade?: string;
}

export interface MarkAttendanceReq {
  applicationId: string;
  comment?: string;
  status: 'Present' | 'Absent' | 'Excused' | 'AWOL';
  mmiCohortId: string;
}

export interface ProApplicationStep {
  name: string;
  data: any;
  finished?: boolean;
}

export interface ProApplicationReviewStep {
  name: string;
  steps: any;
  adminData: any;
}

export enum ProApplicationStatusEnum {
  NotStarted = 'Not started',
  PendingReview = 'Pending Review',
  InProgress = 'In Progress',
  Submitted = 'Submitted',
  InReview = 'In Review',
  Reviewed = 'Reviewed',
  Completed = 'Completed',
}

export interface IEligibility {
  status: 'Eligible' | 'Ineligible' | 'Eligible - not proceeding to stage 2' | 'Referral' | '';
  reasons: string[];
  flags: string[];
}

/** Language in which the applicant completed this application. */
export type ApplicationLanguage = 'en' | 'fr';

export interface ProUserApplication {
  id: string;
  /** Mongo ObjectId as returned by list endpoints (e.g. /applications/all).
   *  Identifiant Mongo tel que renvoyé par les endpoints de liste. */
  _id?: string;
  user: string;
  exam: IProExam;
  phase2Exam: IProExam;
  flags: string[];
  status: ProApplicationStatusEnum;
  phase2Status: ProApplicationStatusEnum;
  applicationNo: string;
  preScreening: PreScreening;
  eligibility: IEligibility;
  phase2Eligibility: IEligibility;
  phase1DecisionMade: any;
  phase2DecisionMade: any;
  steps: ProApplicationStep[];

  /**
   * TDM (Therapeutics Decision Making) fee payment tracking.
   * Suivi du paiement des frais d'examen TDM (prise de décision thérapeutique).
   */
  tdmPayment?: {
    invoiceId?: string;
    status?: 'pending' | 'paid' | 'refunded' | 'waived';
    amount?: number;
    paidAt?: string;
    refundedAt?: string;
    refundReason?: string;
    refundedBy?: string;
  };
  phase2ApplicationSteps: Phase2ApplicationSteps;
  phase1SubmittedDate: string;
  phase2SubmittedDate: string;
  /** Language stream selected by the applicant at the start of the application. */
  language?: ApplicationLanguage;

  /** Phase B — Kye AI Agents (advisory; see kye.model.ts).
   *  Phase B — Agents IA Kye (consultatif ; voir kye.model.ts). */
  aiNotes?: KyeAiNote[];
  /** Phase C — append-only audit trail of screener actions on aiNotes[].
   *  Phase C — journal d'audit en ajout-seul des actions sur les notes IA. */
  aiNoteAuditTrail?: KyeAiNoteAuditEntry[];
  kyeIdentityReview?: KyeIdentitySnapshot | null;
  kyeRotationReview?: KyeRotationSnapshot | null;
  /** Phase D — inline field-level findings projected from structured KYE output.
   *  Advisory only. Append-only per run (keyed by findingId).
   *  Phase D — constats au niveau du champ projetés depuis la sortie structurée KYE. */
  kyeFieldFindings?: KyeFieldFinding[];

  /** Mongo timestamp — used by the Kye header to detect that the application
   *  has been modified since the most recent AI run (staleness badge).
   *  Horodatage Mongo — utilisé par l'en-tête Kye pour détecter qu'une
   *  candidature a été modifiée depuis la dernière exécution IA. */
  updatedAt?: string;

  /** True when a PhysiciansApply record has been linked to this application
   *  (`physicansApplyData.candidateCode2` is populated). Computed server-side
   *  by `ScreenerQueueService.getApplication` and injected into the response.
   *  The raw PA data is excluded from API responses for PII reasons.
   *
   *  Vrai lorsqu'un dossier PhysiciansApply est lié à cette candidature.
   *  Calculé côté serveur et injecté dans la réponse. */
  hasPaData?: boolean;
}

export interface ProApplication {
  id: string;
  _id: string;
  user: string;
  // examSessionId: string;
  exam: string;
  applicationNo: string;
  status: string;
  steps: ProApplicationStep[];
  phaseTwoSteps: ProApplicationStep[];
  eligibility: {
    status: 'Eligible' | 'Ineligible' | 'Eligible - not proceeding to stage 2' | '';
    reasons: string[];
    flags: string[];
  };
  examSessionId: IProExam;
  preScreening: PreScreening;
}

export interface ProApplicationReview {
  _id: string;
  id: string;
  applicationNumber: string;
  firstName: string;
  lastName: string;
  applicationStatus: string;
  currencyOfPractices: string;
  examId: string;
  flagCount: number;
  independentPractices: string;
  isFlagged: boolean;
  noOfPreviousApplication: number;
  referees: string;
  resolvedFlagCount: number;
  reviewStatus: string;
  session: string;
  status: string;
  userId: string;
  year: string;
  application: ProApplicationReviewStep[];
}

export interface Phase2ApplicationSteps {
  personalInformation: PersonalInformationStep;
  certificationsQualifications: CertificationsQualificationsStep;
  languageProficiency: LanguageProficiencyStep;
  postgraduateTraining: PostgraduateTrainingStep;
  rotationEquivalencies?: RotationEquivalenciesStep;
  independentPracticeClinicalExperience: IndependentPracticeClinicalExperienceStep;
  clinicalPracticeSettings: ClinicalPracticeSettingsStep;
  ipPatientPopulation: IpPatientPopulationStep;
  currencyPracticeClinicalExperience: CurrencyPracticeClinicalExperienceStep;
  currencyOfPracticeSettings: CurrencyOfPracticeSettingsStep;
  currencyOfPracticePatientPopulations: CurrencyOfPracticePatientPopulationsStep;
  references: ReferencesStep;
  informationSharing: InformationSharingStep;
}

export interface Phase2ApplicationAdminSteps {
  personalInformation?: PersonalInformationStep;
  certificationsQualifications?: CertificationsQualificationsStep;
  languageProficiency?: LanguageProficiencyStep;
  postgraduateTraining?: PostgraduateTrainingStep;
  independentPracticeClinicalExperience?: IndependentPracticeClinicalExperienceStep;
  clinicalPracticeSettings?: ClinicalPracticeSettingsStep;
  ipPatientPopulation?: IpPatientPopulationStep;
  rotationEquivalencies?: RotationEquivalenciesStep;
  currencyPracticeClinicalExperience?: CurrencyPracticeClinicalExperienceStep;
  currencyOfPracticeSettings?: CurrencyOfPracticeSettingsStep;
  currencyOfPracticePatientPopulations?: CurrencyOfPracticePatientPopulationsStep;
  references?: ReferencesStep;
  informationSharing?: InformationSharingStep;
}

export interface PersonalInformationStep {
  name: string;
  completed: boolean;
  userData: {
    firstName: string;
    middleName: string;
    lastName: string;
    previousNames: string;
    email: string;
    gender: string;
    specificGender: string;
    dateOfBirth: string;
    legalStatus: string;
    citizenship: string;
    countryOfBirth: string;
    motherTongue: string;
    phoneNumber: string;
    streetAddress: string;
    unitNumber: string;
    country: string;
    state: string;
    city: string;
    zipCode: string;
    physiciansApplyCandidateCode: string;
    mincId: string;
    avatar: string;
  };
  adminData: {
    comment: string;
  };
}

export interface CertificationsQualificationsStep {
  name: string;
  completed: boolean;
  userData: {
    advancedCardio: string;
    advancedTrauma: string;
    pediatricAdvanced: string;
    cardioDateOfCompletion: string | null;
    traumaDateOfCompletion: string | null;
    pediatricDateOfCompletion: string | null;
    certifications: Array<{
      nameOfCertification: string;
      briefDescription: string;
      dateOfCompletion: string;
    }>;
  };
  adminData: {
    comment: string;
  };
}

export interface LanguageProficiencyStep {
  name: string;
  completed: boolean;
  userData: {
    fileUpload: string;
    undergraduate: boolean;
    practiceCountry: boolean;
    successfulCompletion: boolean;
    explanationIfNoProof: string;
  };
  adminData: {
    comment: string;
  };
}

export interface PostgraduateTrainingStep {
  name: string;
  completed: boolean;
  userData: {
    pgtFamilyMedicine: PostgraduateTrainingRotation;
    pgtEmergencyMedicine: PostgraduateTrainingRotation;
    pgtGeneralSurgery: PostgraduateTrainingRotation;
    pgtInternalMedicine: PostgraduateTrainingRotation;
    pgtObstetrics: PostgraduateTrainingRotation;
    pgtPediatrics: PostgraduateTrainingRotation;
    pgtPsychiatry: PostgraduateTrainingRotation;
    pgtOthers: {
      others: Array<PostgraduateTrainingRotation & { rotationName: string }>;
    };
    nameOfMedicalSchoolAdmin: string;
    durationOfTrainingProgram: string;
    programStartDate: string;
    programCompletedDate: string;
  };
  adminData: {
    comment: string;
  };
}

export interface PostgraduateTrainingRotation {
  trainingLocation: string;
  otherTrainingLocation: string | null;
  clinic: string;
  city: string;
  province: string;
  country: string;
  startDate: string;
  endDate: string;
  numberOfWeeks: string;
  directCare: string;
  explainCareYouProvided: string | null;
  rotationCompleted: string;
  medicalSchool: string | null;
}

export interface IndependentPracticeClinicalExperienceStep {
  name: string;
  completed: boolean;
  userData: {
    clinicalExperiences: ClinicalExperience[];
  };
  adminData: {
    comment: string;
  };
}

export interface ClinicalExperience {
  _id?: any;
  application?: any;
  nameOfPractice: string;
  key: string;
  roleTitle: string;
  city: string;
  province: string;
  country: string;
  supervisorFirstName: string;
  supervisorLastName: string;
  supervisorTitle: string;
  supervisorEmail: string;
  supervisorNumber: string;
  supervisorSecondaryNumber: string;
  startDate: any;
  endDate: any;
  hoursOfWork: number;
  timeOff: number;
  totalHoursWorked: number;
  clarificationNotes: string | null;
  virtualBasedCare: string;
  responsiblePhysician: string;
  briefDescription: string;
  typeOfPractice: string;
  otherTypeOfPractice: string | null;
  averageVirtualBasedCare: number | null;
  explainDurationOfEmployment: string | null;
  patientReferral: string | null;
  relationship: string;
  notes: any[];
  createdAt?: any;
  updatedAt?: any;
  __v?: number;
}

export interface ClinicalPracticeSettingsStep {
  name: string;
  completed: boolean;
  userData: {
    ambulatoryPractice: string;
    homeVisitOutreach: string;
    hospitalInpatient: string;
    hospitalEmergency: string;
    residentialLongTermCare: string;
    other: string;
    description: string;
    total: number;
  };
  adminData: {
    comment: string;
  };
}

export interface RotationEquivalenciesStep {
  name: string;
  completed: boolean;
  userData: RotationEquivalency[];
  adminData: {
    comment: string;
  };
}

export interface RotationEquivalency {
  _id: string;
  id: string;
  inPersonHours: any[];
  virtualHours: any[];
  notes: any[];
  rotationOneStatus: string;
  rotationName: string;
  applicationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  coverScopeOfMissingRotation?: boolean;
  metThroughEquivalency?: string | null;
  explanationForNoAttestation?: string | null;
  applicantRoleOrTitle?: string;
  descriptionOfCareProvided?: string;
  emailAddressOfReferee?: string;
  endDate?: string;
  firstNameOfReferee?: string;
  lastNameOfReferee?: string;
  nameOfPractice?: string;
  phoneOfReferee?: string;
  positionOfReferee?: string;
  practiceLocation?: string;
  startDate?: string;
}

export interface IpPatientPopulationStep {
  name: string;
  completed: boolean;
  userData: {
    firstContact: boolean;
    patientCare: boolean;
    reproductiveCare: boolean;
    antenatalCare: boolean;
    lowRisk: boolean;
    postpartumCare: boolean;
    newbornHospital: boolean;
    newbornCommunity: boolean;
    comprehensiveCareYouth: boolean;
    comprehensiveCareAdults: boolean;
    comprehensiveCareOlderAdults: boolean;
    palliativeCare: boolean;
    addictionCare: boolean;
    patientsDepartment: boolean;
    patientsOffice: boolean;
    patientsHome: boolean;
    patientsSettings: boolean;
    patientsHomes: boolean;
    careFacilities: boolean;
    communitySettings: boolean;
    responsiblePhysician: boolean;
    performProcedures: boolean;
    medicalCommunity: boolean;
    attestationOne: boolean;
    attestationTwo: boolean;
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyPracticeClinicalExperienceStep {
  name: string;
  completed: boolean;
  userData: {
    currencyOfPracticeExperiences: ClinicalExperience[];
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyOfPracticeSettingsStep {
  name: string;
  completed: boolean;
  userData: {
    ambulatoryPractice: string;
    homeVisitOutreach: string;
    hospitalInpatient: string;
    hospitalEmergency: string;
    residentialLongTermCare: string;
    other: string;
    description: string;
    total: number;
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyOfPracticePatientPopulationsStep {
  name: string;
  completed: boolean;
  userData: {
    firstContact: boolean;
    patientCare: boolean;
    reproductiveCare: boolean;
    antenatalCare: boolean;
    lowRisk: boolean;
    postpartumCare: boolean;
    newbornHospital: boolean;
    newbornCommunity: boolean;
    comprehensiveCareYouth: boolean;
    comprehensiveCareAdults: boolean;
    comprehensiveCareOlderAdults: boolean;
    palliativeCare: boolean;
    addictionCare: boolean;
    patientsDepartment: boolean;
    patientsOffice: boolean;
    patientsHome: boolean;
    patientsSettings: boolean;
    patientsHomes: boolean;
    careFacilities: boolean;
    communitySettings: boolean;
    responsiblePhysician: boolean;
    performProcedures: boolean;
    medicalCommunity: boolean;
    attestationOne: boolean;
    attestationTwo: boolean;
  };
  adminData: {
    comment: string;
  };
}

export interface ReferencesStep {
  name: string;
  completed: boolean;
  userData: {
    referees: Referee[];
  };
  adminData: {
    comment: string;
  };
}

export interface Referee {
  _id?: any;
  key: string;
  salutation: string;
  firstName: string;
  lastName: string;
  position: string;
  primaryTelephone: string;
  secondaryTelephone: string;
  emailAddress: string;
  supervised: string;
  briefDescription: string | null;
  application?: any;
  startDate: any;
  endDate: any;
  notes: any[];
  createdAt?: any;
  updatedAt?: any;
  __v?: number;
}

export interface InformationSharingStep {
  name: string;
  completed: boolean;
  userData: {
    authorization: boolean;
    contentToShare: boolean;
    attestation: boolean;
    acknowledgement: boolean;
    understanding: boolean;
    disclaimer: boolean;
  };
  adminData: {
    comment: string;
  };
}
